import { z } from 'zod';

export const publicSupportChatMessageSchema = z
  .object({
    id: z.uuid(),
    clientMessageId: z.string().nullable().optional(),
    replyToClientMessageId: z.string().nullable().optional(),
    direction: z.enum(['inbound', 'outbound']),
    channel: z.enum(['whatsapp', 'web']),
    body: z.string().min(1),
    senderType: z.enum(['client', 'bot', 'operator']),
    deliveryStatus: z
      .enum(['received', 'pending', 'sent', 'delivered', 'read', 'failed'])
      .nullable(),
    createdAt: z.string().datetime(),
  })
  .strict();

export const publicSupportUnavailableReceiptSchema = z
  .object({
    code: z.literal('support_answer_unavailable'),
    outcome: z.literal('unavailable'),
    retryable: z.literal(true),
    canContinue: z.boolean(),
    inbound: publicSupportChatMessageSchema.extend({
      direction: z.literal('inbound'),
      channel: z.literal('web'),
      senderType: z.literal('client'),
    }),
    error: z.string().min(1),
  })
  .strict();

export const projectChatNextActionSchema = z.enum([
  'continue',
  'collect_contact',
  'select_unit',
  'human_takeover',
]);
const projectChatIntentSchema = z.enum([
  'information',
  'showroom_visit',
  'site_visit',
  'phone_consultation',
  'virtual_meeting',
  'proforma_request',
]);
// Public projection: discard internal actor, policy and trust metadata from the wire payload.
export const projectChatPublicMessageSchema = z.object({
  id: z.uuid(),
  actor: z.enum([
    'anonymous_consumer',
    'publisher_ai',
    'system',
    'publisher_employee',
    'buyer_ai',
    'buyer',
  ]),
  body: z.string().min(1),
  createdAt: z.string().datetime(),
  sourceReferences: z.array(z.string()),
  channelMessageId: z.string().nullable(),
  trustReport: z
    .object({
      webChatDelivery: z
        .object({
          nextAction: projectChatNextActionSchema,
          suggestedIntent: projectChatIntentSchema.nullable(),
        })
        .strict()
        .optional(),
    })
    .nullable(),
});
export type ProjectChatPublicMessage = z.infer<
  typeof projectChatPublicMessageSchema
>;

export const projectChatStartResponseSchema = z
  .object({
    conversationId: z.uuid(),
    expiresAt: z.string().datetime(),
    assistantName: z.string().min(1),
    disclosure: z.string().min(1),
  })
  .strict();
export const projectChatHistoryResponseSchema = projectChatStartResponseSchema
  .extend({
    messages: z.array(projectChatPublicMessageSchema),
    humanTakeover: z.boolean(),
  })
  .strict();
export const projectChatMessageResponseSchema = z
  .object({
    conversationId: z.uuid(),
    userMessage: projectChatPublicMessageSchema,
    message: projectChatPublicMessageSchema,
    humanTakeover: z.boolean(),
    nextAction: projectChatNextActionSchema,
    suggestedIntent: projectChatIntentSchema.nullable(),
  })
  .strict();
